import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';

const API_URL = process.env.REACT_APP_API_URL;

export default function MakeupRequestModal({ show, onClose, courseId }) {
  const [slots, setSlots] = useState([]);
  const [selectedSlotKey, setSelectedSlotKey] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [reasonText, setReasonText] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [includeInadequate, setIncludeInadequate] = useState(false);
  const [canRequest, setCanRequest] = useState(false);
  const [loading, setLoading] = useState(false);
  const [classroomLoading, setClassroomLoading] = useState(false);
  const [classroomOptions, setClassroomOptions] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [selectedClassroomId, setSelectedClassroomId] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (show) {
      setSelectedSlotKey(null);
      setSelectedSlot(null);
      setReasonText('');
      setSelectedFile(null);
      setIncludeInadequate(false);
      setClassroomOptions([]);
      setSuggestions([]);
      setSelectedClassroomId(null);
      setErrorMessage('');
    }
  }, [show]);

  useEffect(() => {
    if (show && courseId) {
      setLoading(true);
      axios
        .get(`${API_URL}/Custom/GetEligibleMakeupSlots`, {
          params: { courseId, includeInadequateClassrooms: includeInadequate },
        })
        .then(({ data }) => setSlots(Array.isArray(data) ? data : []))
        .catch(() => setSlots([]))
        .finally(() => setLoading(false));
    }
  }, [show, courseId, includeInadequate]);

  useEffect(() => {
    setCanRequest(
      !!selectedSlot &&
        selectedClassroomId !== null &&
        reasonText.trim() !== '' &&
        selectedFile !== null
    );
  }, [selectedSlot, selectedClassroomId, reasonText, selectedFile]);

  useEffect(() => {
    if (selectedSlot && show) {
      loadClassrooms(selectedSlot);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeInadequate]);

  const groupedSlots = useMemo(() => {
    const now = new Date();
    const limit = new Date();
    limit.setDate(now.getDate() + 14);
    const map = new Map();

    slots.forEach((s) => {
      const d = new Date(s.date);
      if (d >= now && d <= limit) {
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(s);
      }
    });

    return Array.from(map.entries()).sort(
      (a, b) => new Date(a[0]) - new Date(b[0])
    );
  }, [slots]);

  const formatDateForApi = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const formatDateDisplay = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', weekday: 'long' });
  };

  const formatTime = (value) => {
    if (!value && value !== 0) return '';
    if (typeof value === 'string') {
      const parts = value.split(':');
      if (parts.length >= 2) return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
      try {
        const d = new Date(`1970-01-01T${value}`);
        if (!Number.isNaN(d.getTime())) {
          return d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
        }
      } catch (err) {
        return value;
      }
    }
    if (typeof value === 'object' && value !== null) {
      const hours = value.hours ?? value.Hours ?? 0;
      const minutes = value.minutes ?? value.Minutes ?? 0;
      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    }
    if (typeof value === 'number') {
      const hours = Math.floor(value);
      const minutes = Math.round((value - hours) * 60);
      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    }
    return String(value);
  };

  const slotKey = (slot) => `${slot.id}|${formatDateForApi(slot.date)}`;

  const loadClassrooms = (slotInfo, suggestionToApply = null) => {
    if (!slotInfo || !courseId) return;
    setClassroomLoading(true);
    setClassroomOptions([]);
    setSuggestions([]);
    setSelectedClassroomId(null);
    setErrorMessage('');

    axios
      .get(`${API_URL}/Custom/GetMakeupClassroomOptions`, {
        params: {
          courseId,
          timeSlotId: slotInfo.id,
          date: formatDateForApi(slotInfo.date),
          includeInadequateClassrooms: includeInadequate,
        },
      })
      .then(({ data }) => {
        const options = Array.isArray(data?.classrooms) ? data.classrooms : [];
        setClassroomOptions(options);
        setSuggestions(Array.isArray(data?.suggestions) ? data.suggestions : []);

        if (suggestionToApply) {
          const match = options.find((opt) => opt.id === suggestionToApply.classroom?.id);
          if (match) {
            setSelectedClassroomId(match.id);
          }
        }

        if (!options.length && !suggestionToApply) {
          setErrorMessage('Bu tarih ve saat için uygun sınıf bulunamadı. Aşağıdaki alternatifleri değerlendirebilirsiniz.');
        }
      })
      .catch(() => {
        setClassroomOptions([]);
        setSuggestions([]);
        setErrorMessage('Sınıf bilgileri yüklenemedi.');
      })
      .finally(() => setClassroomLoading(false));
  };

  const handleSlotSelection = (slot) => {
    const key = slotKey(slot);
    setSelectedSlotKey(key);
    setSelectedSlot(slot);
    loadClassrooms(slot);
  };

  const handleUseSuggestion = (suggestion) => {
    if (!suggestion) return;
    const slotInfo = {
      id: suggestion.timeSlotId,
      date: suggestion.date,
      startTime: suggestion.startTime,
      endTime: suggestion.endTime,
    };
    setSelectedSlotKey(slotKey(slotInfo));
    setSelectedSlot(slotInfo);
    loadClassrooms(slotInfo, suggestion);
  };

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0] || null);
  };

  const handleRequest = () => {
    if (!selectedSlot || !selectedClassroomId) return;
    const formData = new FormData();
    formData.append('courseId', courseId);
    formData.append('timeSlotId', selectedSlot.id);
    formData.append('date', formatDateForApi(selectedSlot.date));
    formData.append('classroomId', selectedClassroomId);
    formData.append('reason', reasonText);
    formData.append('evidence', selectedFile);
    formData.append('includeInadequateClassrooms', includeInadequate);

    axios
      .post(`${API_URL}/Custom/RequestMakeup`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then(() => {
        Swal.fire({
          title: 'Talebiniz Gönderildi',
          icon: 'success',
          confirmButtonText: 'Tamam',
        }).then(() => {
          onClose();
          window.location.reload();
        });
      })
      .catch(() => {
        Swal.fire({
          title: 'Hata',
          text: 'Talebiniz gönderilirken bir sorun oluştu. Lütfen tekrar deneyin.',
          icon: 'error',
          confirmButtonText: 'Kapat',
        });
      });
  };

  if (!show) return null;

  const selectedClassroom = classroomOptions.find((c) => c.id === selectedClassroomId);

  return (
    <div
      className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 2000 }}
    >
      <div
        className="modal-content p-4"
        style={{ maxWidth: '90%', width: '500px', borderRadius: '0.5rem' }}
      >
        <div className="modal-header border-0 pb-3">
          <h5 className="modal-title">Telafi Dersi Talebi</h5>
          <button type="button" className="btn-close" onClick={onClose}></button>
        </div>
        <div className="modal-body p-0">
          <div className="form-check form-switch mb-3">
            <input
              className="form-check-input"
              type="checkbox"
              id="includeInadequate"
              checked={includeInadequate}
              onChange={(e) => setIncludeInadequate(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="includeInadequate">
              Kapasitesi yetmeyen sınıfları da göster
            </label>
          </div>
          <div className="mb-3">
            <label className="form-label">Uygun Saatleri Seçiniz:</label>
            <div
              style={{ maxHeight: '220px', overflowY: 'auto', paddingRight: '4px' }}
            >
              {loading ? (
                <div className="text-muted">Yükleniyor...</div>
              ) : groupedSlots.length > 0 ? (
                groupedSlots.map(([date, daySlots]) => (
                  <div key={date} className="mb-3">
                    <div className="fw-semibold mb-1">
                      {new Date(date).toLocaleDateString('tr-TR', {
                        weekday: 'long',
                        day: 'numeric',
                        month: 'numeric',
                      })}
                    </div>
                    {daySlots.map((s) => (
                      <div key={`${s.id}-${s.date}`} className="form-check mb-1 ms-3">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="slot"
                          id={`slot-${s.id}-${s.date}`}
                          checked={selectedSlotKey === slotKey(s)}
                          onChange={() => handleSlotSelection(s)}
                        />
                        <label
                          className="form-check-label"
                          htmlFor={`slot-${s.id}-${s.date}`}
                        >
                          {formatTime(s.startTime)} – {formatTime(s.endTime)}
                          {s.classroomName && (
                            <span className="text-muted ms-2">{s.classroomName}</span>
                          )}
                          {includeInadequate && !s.hasAdequateClassroom && (
                            <span className="badge bg-warning text-dark ms-2">Kapasite yetersiz</span>
                          )}
                        </label>
                      </div>
                    ))}
                  </div>
                ))
              ) : (
                <div className="text-muted">Uygun saat bulunamadı</div>
              )}
            </div>
          </div>
          {selectedSlot && (
            <div className="alert alert-secondary py-2">
              <div className="fw-semibold">Seçilen Telafi:</div>
              <div>
                {formatDateDisplay(selectedSlot.date)} · {formatTime(selectedSlot.startTime)} - {formatTime(selectedSlot.endTime)}
                {selectedClassroom && ` · ${selectedClassroom.name} (${selectedClassroom.capacity})`}
              </div>
            </div>
          )}
          {classroomLoading ? (
            <div className="text-muted">Sınıflar yükleniyor...</div>
          ) : selectedSlot ? (
            <div className="mb-3">
              <label className="form-label">Sınıf Seçimi:</label>
              {errorMessage && <div className="alert alert-warning py-2">{errorMessage}</div>}
              {classroomOptions.length > 0 ? (
                <div className="d-flex flex-column gap-2">
                  {classroomOptions.map((c) => (
                    <label key={c.id} className="form-check">
                      <input
                        type="radio"
                        className="form-check-input"
                        name="classroom"
                        checked={selectedClassroomId === c.id}
                        onChange={() => setSelectedClassroomId(c.id)}
                      />
                      <span className="form-check-label">
                        {c.name} ({c.capacity})
                        {includeInadequate && !c.isAdequate && (
                          <span className="badge bg-warning text-dark ms-2">Kapasite yetersiz</span>
                        )}
                      </span>
                    </label>
                  ))}
                </div>
              ) : (
                !errorMessage && <div className="text-muted">Uygun sınıf bulunamadı.</div>
              )}
            </div>
          ) : null}
          {suggestions.length > 0 && (
            <div className="alert alert-info">
              <div className="fw-semibold mb-2">Önerilen alternatifler</div>
              <div className="d-flex flex-column gap-2">
                {suggestions.map((sugg, idx) => (
                  <div key={`${sugg.timeSlotId}-${sugg.date}-${idx}`} className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                    <div>
                      <div>{formatDateDisplay(sugg.date)}</div>
                      <div>
                        {formatTime(sugg.startTime)} - {formatTime(sugg.endTime)} · {sugg.classroom?.name}
                        {sugg.classroom?.capacity ? ` (${sugg.classroom.capacity})` : ''}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => handleUseSuggestion(sugg)}
                    >
                      Bu öneriyi seç
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="mb-3">
            <label className="form-label">Gerekçe:</label>
            <textarea
              className="form-control"
              rows={3}
              value={reasonText}
              onChange={(e) => setReasonText(e.target.value)}
              placeholder="Telafi gerekçenizi yazınız"
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Kanıt Dosyası (zorunlu):</label>
            <input type="file" className="form-control" onChange={handleFileChange} required />
          </div>
        </div>
        <div className="modal-footer border-0 pt-3">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Kapat
          </button>
          <button
            type="button"
            className="btn btn-success"
            onClick={handleRequest}
            disabled={!canRequest}
          >
            Talebi Gönder
          </button>
        </div>
      </div>
    </div>
  );
}
